import React from 'react';
import { createUseStyles } from 'react-jss';
import { S, A } from './tokens';

type Props = {
  /** 1-based. */
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  onPageSize?: (size: number) => void;
  sizes?: number[];
  noun?: string;
};

const H = 32;

const useStyles = createUseStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px 20px',
    flexWrap: 'wrap',
    padding: '14px 2px 24px',
    fontFamily: S.ui,
    fontSize: 13,
    color: S.muted
  },
  range: { fontVariantNumeric: 'tabular-nums', '& b': { color: S.fg, fontWeight: 600 } },
  right: { display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' },
  // "Rows per page" with a select drawn like the admin's fields; the native control stays for keyboards and screen readers.
  size: { display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' },
  selectWrap: {
    position: 'relative',
    display: 'inline-flex',
    '& .material-icons': { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: S.muted, pointerEvents: 'none' }
  },
  select: {
    appearance: 'none',
    WebkitAppearance: 'none',
    height: H,
    padding: '0 28px 0 10px',
    border: `1px solid ${S.fieldLine}`,
    borderRadius: A.radius,
    background: S.field,
    fontFamily: S.ui,
    fontSize: 13,
    fontWeight: 600,
    color: S.fg,
    cursor: 'pointer',
    '&:hover': { borderColor: S.muted },
    '&:focus-visible': A.focus
  },
  nav: { display: 'flex', alignItems: 'center', gap: 4 },
  btn: {
    all: 'unset',
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    minWidth: H,
    height: H,
    padding: '0 8px',
    borderRadius: A.radius,
    fontFamily: S.ui,
    fontSize: 13,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    color: S.fg,
    cursor: 'pointer',
    transition: 'background-color 120ms ease',
    '& .material-icons': { fontSize: 18 },
    '&:hover': { background: S.chromeHover },
    '&:focus-visible': A.focus,
    '&[disabled]': { color: S.muted, opacity: 0.5, cursor: 'default', background: 'none' }
  },
  step: { padding: '0 10px 0 6px', '&$next': { padding: '0 6px 0 10px' } },
  next: {},
  current: {
    background: S.fg,
    color: S.onFg,
    cursor: 'default',
    '&:hover': { background: S.fg }
  },
  gap: { minWidth: 20, textAlign: 'center', color: S.muted, userSelect: 'none' },
  // On narrow screens only "Page 3 of 6" and the arrows.
  numbers: { display: 'contents', '@media (max-width: 720px)': { display: 'none' } },
  compact: { display: 'none', padding: '0 6px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', '@media (max-width: 720px)': { display: 'inline' } },
  label: { '@media (max-width: 720px)': { display: 'none' } }
});

/**
 * The page numbers to show: always the first and last, the current one with a
 * neighbour on each side, and a gap marker ("…") for anything skipped. Seven
 * or fewer pages are all shown.
 */
export const pageItems = (page: number, pages: number): Array<number | 'gap'> => {
  if (pages <= 7) return Array.from({ length: pages }, (_x, i) => i + 1);
  const keep = new Set([1, pages, page - 1, page, page + 1]);
  // Near either end, show a run of five so the bar doesn't change width as you step through.
  if (page <= 4) [2, 3, 4, 5].forEach(n => keep.add(n));
  if (page >= pages - 3) [pages - 4, pages - 3, pages - 2, pages - 1].forEach(n => keep.add(n));
  const sorted = Array.from(keep).filter(n => n >= 1 && n <= pages).sort((a, b) => a - b);
  const out: Array<number | 'gap'> = [];
  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1] > 1) out.push(n - sorted[i - 1] === 2 ? n - 1 : 'gap');
    out.push(n);
  });
  return out;
};

/** "26–50 of 1,107 pages", the page numbers with Previous/Next, and rows per page. */
const Pager: React.FC<Props> = ({ page, pageSize, total, onPage, onPageSize, sizes = [10, 25, 50, 100], noun = 'items' }) => {
  const c = useStyles();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const go = (n: number) => { const p = Math.min(pages, Math.max(1, n)); if (p !== page) onPage(p); };

  return (
    <nav className={c.root} aria-label="Pagination" data-testid="pager">
      <span className={c.range} data-testid="pager-range">
        <b>{from.toLocaleString()}–{to.toLocaleString()}</b> of <b>{total.toLocaleString()}</b> {noun}
      </span>
      <div className={c.right}>
        {onPageSize && (
          <label className={c.size}>
            Rows per page
            <span className={c.selectWrap}>
              <select className={c.select} value={pageSize} onChange={e => onPageSize(Number(e.target.value))} aria-label="Rows per page">
                {Array.from(new Set([...sizes, pageSize])).sort((a, b) => a - b).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <i className="material-icons" aria-hidden>expand_more</i>
            </span>
          </label>
        )}
        {pages > 1 && (
          <div className={c.nav}>
            <button type="button" className={`${c.btn} ${c.step}`} onClick={() => go(page - 1)} disabled={page <= 1} aria-label="Previous page" data-testid="pager-prev">
              <i className="material-icons" aria-hidden>chevron_left</i>
              <span className={c.label}>Previous</span>
            </button>
            <span className={c.numbers}>
              {pageItems(page, pages).map((n, i) => (n === 'gap'
                ? <span key={`gap-${i}`} className={c.gap} aria-hidden>…</span>
                : (
                  <button
                    key={n}
                    type="button"
                    className={`${c.btn} ${n === page ? c.current : ''}`}
                    onClick={() => go(n)}
                    aria-label={`Page ${n}`}
                    aria-current={n === page ? 'page' : undefined}
                    data-page={n}
                  >
                    {n.toLocaleString()}
                  </button>
                )))}
            </span>
            <span className={c.compact}>Page {page.toLocaleString()} of {pages.toLocaleString()}</span>
            <button type="button" className={`${c.btn} ${c.step} ${c.next}`} onClick={() => go(page + 1)} disabled={page >= pages} aria-label="Next page" data-testid="pager-next">
              <span className={c.label}>Next</span>
              <i className="material-icons" aria-hidden>chevron_right</i>
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Pager;
